import React, { useState, useMemo } from 'react';
import { FaUserTie, FaChartBar, FaCrown, FaUsers } from 'react-icons/fa';

// --- Reusable Components ---

// 1. Employee Profile Chip
const EmployeeChip = ({ name, role, color }) => (
  <span 
    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium m-0.5 shadow-sm ${color}`}
    title={role}
  >
    {name.split(' ')[0]}
  </span>
);

// 2. The 9-Box Grid Component
const NineBoxGrid = ({ employees }) => {
  // Mapping logic: Performance (Low, Medium, High) x Potential (Low, Medium, High)
  const gridMap = {
    "Low-Low": { name: "Risk", color: "bg-red-200 border-red-500", role: "Monitor" },
    "Low-Medium": { name: "Inconsistent", color: "bg-yellow-200 border-yellow-500", role: "Coach" },
    "Low-High": { name: "Potential", color: "bg-blue-200 border-blue-500", role: "Develop" },
    "Medium-Low": { name: "Contributor", color: "bg-gray-200 border-gray-500", role: "Reward" },
    "Medium-Medium": { name: "Core", color: "bg-indigo-200 border-indigo-500", role: "Sustain" },
    "Medium-High": { name: "Future Star", color: "bg-teal-200 border-teal-500", role: "Fast-Track" },
    "High-Low": { name: "Specialist", color: "bg-lime-200 border-lime-500", role: "Retain" },
    "High-Medium": { name: "High Performer", color: "bg-cyan-200 border-cyan-500", role: "Engage" },
    "High-High": { name: "Star", color: "bg-green-200 border-green-500", role: "Promote" },
  };

  const getQuadrant = (perf, pot) => {
    // Simple logic: 1-3=Low, 4-7=Medium, 8-10=High
    const perfKey = perf <= 3 ? 'Low' : perf <= 7 ? 'Medium' : 'High';
    const potKey = pot <= 3 ? 'Low' : pot <= 7 ? 'Medium' : 'High';
    return `${perfKey}-${potKey}`;
  };

  const categorizedEmployees = employees.reduce((acc, emp) => {
    const key = getQuadrant(emp.performance, emp.potential);
    acc[key] = [...(acc[key] || []), emp];
    return acc;
  }, {});

  const quadrants = ['High', 'Medium', 'Low'];
  const dimensions = ['Low', 'Medium', 'High'];

  return (
    <div className="overflow-x-auto">
      <div className="grid grid-cols-4 border border-gray-300 w-full min-w-[700px] shadow-xl rounded-lg">
        {/* Y-Axis Label */}
        <div className="col-span-1 flex flex-col justify-around items-center text-sm font-semibold text-gray-700 bg-gray-50">
          <div className="w-full text-center py-4">High Potential</div>
          <div className="w-full text-center py-4">Medium Potential</div>
          <div className="w-full text-center py-4">Low Potential</div>
        </div>

        {/* The 9 Boxes */}
        <div className="col-span-3 grid grid-cols-3">
          {quadrants.map(pot =>
            dimensions.map(perf => {
              const key = `${perf}-${pot}`;
              const { name, color } = gridMap[key];
              const quadEmps = categorizedEmployees[key] || [];

              return (
                <div key={key} className={`p-3 h-48 border border-gray-200 flex flex-col ${color}`}>
                  <h3 className="text-base font-bold mb-1">{name} ({quadEmps.length})</h3>
                  <p className="text-xs text-gray-700 italic mb-2">Strategy: {gridMap[key].role}</p>
                  <div className="flex flex-wrap overflow-y-auto max-h-24">
                    {quadEmps.map(emp => (
                      <EmployeeChip key={emp.id} name={emp.name} role={emp.role} color="bg-white text-gray-800" />
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* X-Axis Label */}
        <div className="col-span-4 grid grid-cols-3 text-center text-sm font-semibold text-gray-700 bg-gray-50 border-t border-gray-300 ml-[25%]">
          <div className="py-2">Low Performance</div>
          <div className="py-2">Medium Performance</div>
          <div className="py-2">High Performance</div>
        </div>
      </div>
    </div>
  );
};

// --- Dummy Data (Indian Context) ---
const mockEmployees = [
  { id: 1, name: 'Vikram Singh', role: 'Dev Lead', performance: 9, potential: 9 }, // Star
  { id: 2, name: 'Asha Reddy', role: 'HR Executive', performance: 7, potential: 8 }, // Future Star
  { id: 3, name: 'Rajesh Nair', role: 'Sr Analyst', performance: 4, potential: 5 }, // Core
  { id: 4, name: 'Pooja Patil', role: 'Sales Rep', performance: 8, potential: 3 }, // Specialist
  { id: 5, name: 'Kunal Verma', role: 'Jr Developer', performance: 3, potential: 8 }, // Potential
  { id: 6, name: 'Anil Das', role: 'Manager', performance: 5, potential: 2 }, // Risk (Performance Low)
  { id: 7, name: 'Shreya Iyer', role: 'Tech Architect', performance: 9, potential: 7 }, // High Performer
];

// --- Main Dashboard Component ---

const TalentManagement = () => {
  const [employees] = useState(mockEmployees);
  const totalHiPo = employees.filter(e => e.potential >= 7 && e.performance >= 7).length; // High Performance, High Potential

  return (
    <div className="min-h-screen p-4 sm:p-6 md:p-8 bg-gray-50" style={{ backgroundColor: '#F3F4F6' }}>
      <h1 className="text-3xl font-extrabold text-gray-800 mb-6 border-b pb-2">Talent Management Dashboard ✨</h1>
      
      {/* 1. Summary Cards Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="p-5 rounded-xl shadow-md bg-white border-l-4 border-green-500">
            <p className="text-sm font-semibold text-gray-700">Total High-Potential (HiPo)</p>
            <p className="mt-2 text-3xl font-bold text-gray-900">{totalHiPo}</p>
        </div>
        <div className="p-5 rounded-xl shadow-md bg-white border-l-4 border-blue-500">
            <p className="text-sm font-semibold text-gray-700">Succession Readiness Score</p>
            <p className="mt-2 text-3xl font-bold text-gray-900">78%</p>
        </div>
        <div className="p-5 rounded-xl shadow-md bg-white border-l-4 border-yellow-500">
            <p className="text-sm font-semibold text-gray-700">Key Roles with Coverage</p>
            <p className="mt-2 text-3xl font-bold text-gray-900">8 / 10</p>
        </div>
        <div className="p-5 rounded-xl shadow-md bg-white border-l-4 border-red-500">
            <p className="text-sm font-semibold text-gray-700">Talent Risk (HiPo Exits YTD)</p>
            <p className="mt-2 text-3xl font-bold text-gray-900">1</p>
        </div>
      </div>

      {/* 2. The 9-Box Grid Visualization */}
      <div className="bg-white p-6 rounded-xl shadow-lg mb-8 overflow-x-auto">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center"><FaChartBar className="mr-2 text-indigo-600" /> Talent Assessment (9-Box Grid)</h2>
        <NineBoxGrid employees={employees} />
      </div>

      {/* 3. Succession Planning & HiPo Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Succession Planning Table */}
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Key Role Succession Plan</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Candidate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Readiness</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {[{ role: 'VP Operations', cand: 'Vikram Singh', ready: 'Ready Now', color: 'bg-green-100 text-green-800' },
                  { role: 'Head of Sales', cand: 'Shreya Iyer', ready: '1 Year', color: 'bg-yellow-100 text-yellow-800' },
                  { role: 'Chief Architect', cand: 'Asha Reddy', ready: '3+ Years', color: 'bg-blue-100 text-blue-800' },
                ].map((item, index) => (
                  <tr key={index} className="hover:bg-indigo-50 transition duration-150">
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.role}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-indigo-700 font-semibold">{item.cand}</td>
                    <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${item.color}`}>
                            {item.ready}
                        </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* HiPo Development List */}
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h2 className="text-xl font-bold text-gray-800 mb-4">High-Potential (HiPo) Development Program</h2>
          <ul className="space-y-3">
              {employees.filter(e => e.potential >= 7).sort((a,b) => b.potential - a.potential).map((emp) => (
                  <li key={emp.id} className="flex justify-between items-center p-3 border border-gray-100 rounded-lg hover:bg-gray-50 transition duration-150">
                      <div className="flex flex-col">
                          <span className="font-semibold text-gray-800">{emp.name}</span>
                          <span className="text-xs text-gray-500">{emp.role}</span>
                      </div>
                      <span className="text-sm font-bold text-teal-600 flex items-center">
                          <FaCrown className="mr-1" /> Potential: {emp.potential}/10
                      </span>
                  </li>
              ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TalentManagement;